import {
  AlertCircle,
  Baby,
  BadgeCheck,
  Check,
  CircleDollarSign,
  Clock,
  Images,
  Lock,
  ShieldAlert,
  ShieldCheck,
  Store,
  TriangleAlert,
  Users
} from 'lucide-react';
import {
  Badge,
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@kroll-resolver-crisp/ui-components-react';
import {type Item, type SubmissionStatusGroup} from '@/api/items';
import {type ImageMatchTier, imageMatchTierLabels} from '@/api/jobs';
import {ITEM_NOUN, VERIFIED_LABEL} from '@/lib/platform';
import {statusLabel, statusTone, TONE_BADGE} from '@/lib/status';
import {
  getMetaBoolean,
  getMetaFieldAny,
  AGE_RESTRICTED_FIELD_NAMES,
  isChannelMonetized,
  VERIFIED_FIELD_NAMES,
  VERIFIED_MERCHANT_FIELD_NAMES,
  MADE_FOR_KIDS_FIELD_NAMES,
  SAFE_FOR_MINORS_FIELD_NAMES,
  CONTENT_WARNING_FIELD_NAMES,
  CONTENT_WARNING_REASON_FIELD_NAMES
} from '@/lib/itemMetadata';
import {cn} from '@/lib/utils';

// The compact card's flag indicators, sized to its title row. A status colour is a surface token
// (--success fails AA as ink), so it fills a chip and the icon takes the matching foreground;
// destructive follows the design system's own tinted badge treatment. Primary ink and muted are
// text tokens, so those indicators stay as bare icons.
const FLAG_CHIP = 'flex size-4 shrink-0 items-center justify-center';
const FLAG_CHIP_SUCCESS = cn(FLAG_CHIP, TONE_BADGE.success);
const FLAG_CHIP_DESTRUCTIVE = cn(FLAG_CHIP, TONE_BADGE.destructive);

// Exact and PageConfirmed both mean "this item's own thumbnail matched" and read as filled; the two
// weaker tiers deliberately recede to an outline.
export const IMAGE_MATCH_TIER_VARIANT: Record<ImageMatchTier, 'default' | 'secondary' | 'outline'> =
  {
    Exact: 'default',
    PageConfirmed: 'secondary',
    Partial: 'outline',
    PageOnly: 'outline'
  };

export const IMAGE_MATCH_TIER_TOOLTIPS: Record<ImageMatchTier, string> = {
  Exact: `The searched image is this ${ITEM_NOUN}’s own thumbnail.`,
  PageConfirmed: `Found on this ${ITEM_NOUN}’s own page, matching its own thumbnail.`,
  Partial: `A crop or other derivative of the searched image matched this ${ITEM_NOUN}’s thumbnail.`,
  PageOnly: `Found on a page for this ${ITEM_NOUN}, but the image that matched was not this ${ITEM_NOUN}’s thumbnail — most often a recommended ${ITEM_NOUN} nearby. Weakest evidence.`
};

/** How this result was matched to the searched image. Only rendered for image_search jobs. */
// Shown wherever an item appears once its thumbnail has been replaced, so an analyst knows the image
// they see now may not be the one that was collected or reported - the history dialog has both.
export function ThumbnailChangedBadge({className}: {readonly className?: string}) {
  return (
    <Badge variant="outline" className={cn('bg-card gap-1', className)}>
      <Images className="size-3" />
      Thumbnail changed
    </Badge>
  );
}

export function ImageMatchTierBadge({tier}: {readonly tier: ImageMatchTier}) {
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Badge
            variant={IMAGE_MATCH_TIER_VARIANT[tier]}
            className={cn('shrink-0', tier === 'PageOnly' && 'text-muted-foreground')}
          />
        }>
        {imageMatchTierLabels[tier]}
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">{IMAGE_MATCH_TIER_TOOLTIPS[tier]}</TooltipContent>
    </Tooltip>
  );
}

export function ItemFlags({
  item,
  matchTier
}: {
  readonly item: Item;
  readonly matchTier?: ImageMatchTier | undefined;
}) {
  const ageRestricted = getMetaBoolean(item.metadata, AGE_RESTRICTED_FIELD_NAMES);
  const monetized = isChannelMonetized(item.metadata, item.content_type === 'actor');
  const verified = getMetaBoolean(item.metadata, VERIFIED_FIELD_NAMES);
  const verifiedMerchant = getMetaBoolean(item.metadata, VERIFIED_MERCHANT_FIELD_NAMES);
  const madeForKids = getMetaBoolean(item.metadata, MADE_FOR_KIDS_FIELD_NAMES);
  const safeForMinors = getMetaBoolean(item.metadata, SAFE_FOR_MINORS_FIELD_NAMES);
  const contentWarning = getMetaBoolean(item.metadata, CONTENT_WARNING_FIELD_NAMES);
  const contentWarningReason = getMetaFieldAny(item.metadata, CONTENT_WARNING_REASON_FIELD_NAMES);
  const isChannel = item.content_type === 'actor';

  if (
    !matchTier &&
    !isChannel &&
    !ageRestricted &&
    !monetized &&
    !verified &&
    !verifiedMerchant &&
    !madeForKids &&
    !safeForMinors &&
    !contentWarning
  ) {
    return null;
  }

  return (
    <div className="flex shrink-0 items-center gap-1">
      {matchTier ? <ImageMatchTierBadge tier={matchTier} /> : null}
      {isChannel ? (
        <Tooltip>
          <TooltipTrigger
            render={
              <span className="text-muted-foreground flex size-4 items-center justify-center" />
            }>
            <Users className="size-3.5" role="img" aria-label="Channel result" />
          </TooltipTrigger>
          <TooltipContent>Channel result</TooltipContent>
        </Tooltip>
      ) : null}
      {ageRestricted ? (
        <Tooltip>
          <TooltipTrigger render={<span className={FLAG_CHIP_DESTRUCTIVE} />}>
            <ShieldAlert className="size-3" role="img" aria-label="Age restricted" />
          </TooltipTrigger>
          <TooltipContent>Age restricted</TooltipContent>
        </Tooltip>
      ) : null}
      {monetized ? (
        <Tooltip>
          <TooltipTrigger render={<span className={FLAG_CHIP_SUCCESS} />}>
            <CircleDollarSign className="size-3" role="img" aria-label="Channel monetised" />
          </TooltipTrigger>
          <TooltipContent>Channel monetised</TooltipContent>
        </Tooltip>
      ) : null}
      {verified ? (
        <Tooltip>
          <TooltipTrigger
            render={<span className="text-primary-ink flex size-4 items-center justify-center" />}>
            <BadgeCheck className="h-3.5 w-3.5" aria-label={VERIFIED_LABEL} />
          </TooltipTrigger>
          <TooltipContent>{VERIFIED_LABEL}</TooltipContent>
        </Tooltip>
      ) : null}
      {verifiedMerchant ? (
        <Tooltip>
          <TooltipTrigger
            render={<span className="text-primary-ink flex size-4 items-center justify-center" />}>
            <Store className="h-3.5 w-3.5" aria-label="Verified merchant" />
          </TooltipTrigger>
          <TooltipContent>Verified merchant</TooltipContent>
        </Tooltip>
      ) : null}
      {madeForKids ? (
        <Tooltip>
          <TooltipTrigger
            render={<span className="text-primary-ink flex size-4 items-center justify-center" />}>
            <Baby className="h-3.5 w-3.5" role="img" aria-label="Made for kids" />
          </TooltipTrigger>
          <TooltipContent>Made for kids</TooltipContent>
        </Tooltip>
      ) : null}
      {safeForMinors ? (
        <Tooltip>
          <TooltipTrigger render={<span className={FLAG_CHIP_SUCCESS} />}>
            <ShieldCheck className="size-3" role="img" aria-label="Safe for minors" />
          </TooltipTrigger>
          <TooltipContent>Safe for minors</TooltipContent>
        </Tooltip>
      ) : null}
      {contentWarning ? (
        <Tooltip>
          <TooltipTrigger render={<span className={FLAG_CHIP_DESTRUCTIVE} />}>
            <TriangleAlert
              className="size-3"
              role="img"
              aria-label={contentWarningReason || 'Content warning'}
            />
          </TooltipTrigger>
          <TooltipContent>{contentWarningReason || 'Content warning'}</TooltipContent>
        </Tooltip>
      ) : null}
    </div>
  );
}

// ── Submission Status Tags (which vertical(s) are sent/in hopper/failed) ──────

export function getSubmissionStatusTags(groups: readonly SubmissionStatusGroup[]) {
  // Each vertical the item was classified into keeps its own tag, since different verticals on the
  // same item can be in different delivery states (e.g. sent under Hate, still in hopper under
  // Harassment) - showing one combined tag would misattribute the wrong status to a vertical.
  return (
    groups
      // Only 'removed' (QC-rejected / analyst-withdrawn) is not a live submission; every other
      // state - including pending, escalated and pending QC - means the item has been submitted.
      .filter((group) => group.status !== 'removed')
      .map((group) => ({
        status: group.status,
        label: group.policy_violations.length > 0 ? group.policy_violations.join(', ') : null,
        submittedAt: group.submitted_at
      }))
  );
}

export function SubmissionStatusTags({
  tags,
  className,
  wrap = true
}: {
  readonly tags: ReturnType<typeof getSubmissionStatusTags>;
  readonly className?: string;
  readonly wrap?: boolean;
}) {
  if (tags.length === 0) {
    return null;
  }

  return (
    <div className={cn('flex gap-1', wrap && 'flex-wrap items-center', className)}>
      {tags.map((tag) => {
        const statusText = statusLabel(tag.status);
        const text = tag.label ? `${tag.label} · ${statusText}` : statusText;
        // SubmittedAt only ever comes back non-null when a historic row backs this group (see
        // ItemsController) - shown as a bonus hover detail, not a different badge identity.
        const title = tag.submittedAt
          ? `${text} (sent to client ${new Date(tag.submittedAt).toLocaleDateString('en-GB')})`
          : text;
        return (
          <Badge
            key={tag.status}
            className={cn('max-w-full justify-start', TONE_BADGE[statusTone(tag.status)])}>
            {tag.status === 'sent to client' ? (
              <Check />
            ) : tag.status === 'delivery failed' || tag.status === 'not sent - removed' ? (
              <AlertCircle />
            ) : (
              <Clock />
            )}
            <span title={title} className="min-w-0 truncate">
              {text}
            </span>
          </Badge>
        );
      })}
    </div>
  );
}

// Another analyst has this item open in the moderation queue. Classifying it meanwhile takes the
// task away from them, so say who and for how long before the analyst does.
export function ItemQueueLeaseBadge({
  item,
  className
}: {
  readonly item: Item;
  readonly className?: string;
}) {
  const leases = item.queue_leases ?? [];
  if (leases.length === 0) {
    return null;
  }

  const names = [...new Set(leases.map((lease) => lease.owner_name))].join(', ');
  const detail = leases
    .map(
      (lease) =>
        `${lease.owner_name} is reviewing it in ${lease.policy_violation} (held until ${new Date(lease.expires_at).toLocaleTimeString()})`
    )
    .join('. ');
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Badge variant="secondary" className={cn('gap-1', className)}>
            <Lock className="size-3" />
            In {names}&apos;s queue
          </Badge>
        }
      />
      <TooltipContent>{detail}</TooltipContent>
    </Tooltip>
  );
}

// Small "Channel sent" chip beside a video's channel name. Says "Channel" in the text and the tooltip
// spells out that the video itself was not submitted, so it can't be read as the video's status.
export function ChannelSubmittedBadge({
  groups,
  className
}: {
  readonly groups: readonly SubmissionStatusGroup[] | undefined;
  readonly className?: string;
}) {
  const tags = getSubmissionStatusTags(groups ?? []);
  const first = tags[0];
  if (!first) {
    return null;
  }

  const detail = tags
    .map((tag) =>
      tag.label ? `${tag.label} · ${statusLabel(tag.status)}` : statusLabel(tag.status)
    )
    .join('; ');
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <span
            className={cn(
              TONE_BADGE[statusTone(first.status)],
              'inline-flex shrink-0 items-center gap-1 rounded px-1.5 py-0.5 text-xs font-medium',
              className
            )}
          />
        }>
        <Users aria-hidden className="size-3" />
        Channel {statusLabel(first.status).toLowerCase()}
      </TooltipTrigger>
      <TooltipContent>
        This channel has been submitted ({detail}). This video has not been submitted.
      </TooltipContent>
    </Tooltip>
  );
}
